import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { taxes } from '../api/endpoints.js';
import { type TaxDetail } from '../api/schemas.js';

export function TaxDetailPage(): React.JSX.Element {
  const id = useParams().taxId ?? '';
  const query = taxes.useItem(id);
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const remove = taxes.useSoftDelete();

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.TAX_READ}
      parent={{ to: '/taxes', label: 'Taxes' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.TAX_UPDATE}>
            <Button asChild variant="outline">
              <Link to={`/taxes/${id}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.TAX_DELETE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data: TaxDetail) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete tax"
            description={`${data.name} will be disabled and hidden from future quotes.`}
            confirmLabel="Delete"
            destructive
            loading={remove.isPending}
            onConfirm={() => {
              void remove.mutateAsync(id).then(() => {
                setConfirmingDelete(false);
                void navigate('/taxes');
              });
            }}
          />
          <DetailGrid>
            <DetailField label="Property">{data.propertyId}</DetailField>
            <DetailField label="Type">{data.type}</DetailField>
            <DetailField label="Basis">{data.basis}</DetailField>
            <DetailField label="Rate">{data.rate ?? '—'}</DetailField>
            <DetailField label="Amount">
              {data.amount ? `${data.amount} ${data.currency}` : '—'}
            </DetailField>
            <DetailField label="Priority">{data.priority}</DetailField>
            <DetailField label="Inclusive">{data.inclusive ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Compounds">{data.compounds ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Applies to fees">{data.appliesToFees ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Enabled">{data.enabled ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Version">{data.version}</DetailField>
          </DetailGrid>
        </>
      )}
    </ResourceDetailPage>
  );
}
